var _a, _b, _c;
import { __decorate, __metadata } from "tslib";
import { Component } from 'decorators';
import view from './promptInput.view.html';
import './promptInput.scss';
import { isMemoryTypePrompt, role, selectableRoles } from '../../../../types/instructions';
import { BUILDER_AGENT_CHATHISTORY_CHANGED } from '../../../../events';
import { AgentSegmentService } from '../../../../services/AgentSegmentService';
import { AgentContextService } from '../../../../services/AgentContextService';
import { AiAgentSidebarStateService } from '../../../../services/AiAgentSidebarStateService';
import { BuilderFeatures } from '../../../../../BuilderFeatures';
import { extractVariableName } from '../../../../utils/variableUtils';
// Static icon mapping to avoid repeated function calls during Angular digest cycles
const ROLE_ICON_MAP = {
    [role.system]: 'ideas-sparkle-ai',
    [role.human]: 'user-default',
    [role.ai]: 'message-sparkle-ai',
    [role.shortTermMemory]: 'restore',
    [role.variable]: 'restore',
};
let PromptInput = class PromptInput {
    static $inject = ['AgentSegmentService', 'AgentContextService', 'AiAgentSidebarStateService', 'clipboard'];
    //#endregion
    /**
     * Handle variable selection from sidebar
     */
    handleSelectedVariable(sidebarData) {
        const targetPromptId = sidebarData.targetPromptId;
        // Only update if this is THE SPECIFIC prompt being edited
        if (!targetPromptId || this.prompt.promptId !== targetPromptId) {
            return;
        }
        const selectedVar = sidebarData.selectedVariable;
        const formattedVariable = `{{${selectedVar}}}`;
        if (this.prompt.type === role.variable && 'content' in this.prompt) {
            this.handleVariableRoleUpdate(formattedVariable);
        }
        else if (this.prompt.type === role.shortTermMemory && 'content' in this.prompt) {
            this.handleShortTermMemoryUpdate(selectedVar, formattedVariable, sidebarData);
        }
    }
    /**
     * Handle variable role update
     */
    handleVariableRoleUpdate(formattedVariable) {
        if (!('content' in this.prompt)) {
            return;
        }
        if (this.prompt.content === formattedVariable) {
            return;
        }
        this.prompt.content = formattedVariable;
        if (this.onSavePrompt) {
            this.onSavePrompt({ instruction: this.prompt });
        }
    }
    /**
     * Handle short-term memory variable update
     */
    handleShortTermMemoryUpdate(selectedVar, formattedVariable, sidebarData) {
        if (!('content' in this.prompt)) {
            return;
        }
        if (this.prompt.content === formattedVariable) {
            return;
        }
        this.selectedHistoryVariable = selectedVar;
        this.prompt.content = formattedVariable;
        if (this.onSavePrompt) {
            this.onSavePrompt({ instruction: this.prompt });
        }
        // Open config sidebar after saving
        this.openConfigSidebarAfterVariableSelection(sidebarData);
    }
    /**
     * Open config sidebar after variable selection
     */
    openConfigSidebarAfterVariableSelection(sidebarData) {
        const historyLength = sidebarData.selectedVariableHistoryLength || 100;
        const currentConfig = this.getAdjustedConfig(historyLength);
        this.AiAgentSidebarStateService.open('historyConfig', {
            currentConfig: currentConfig,
            promptId: this.prompt.promptId,
            maxHistoryLength: historyLength,
        });
    }
    /**
     * Get adjusted config for prompt
     */
    getAdjustedConfig(historyLength) {
        if (!('config' in this.prompt) || !this.prompt.config) {
            return null;
        }
        let adjustedLength = this.prompt.config.length;
        if (adjustedLength > historyLength) {
            adjustedLength = historyLength;
        }
        return {
            length: adjustedLength,
            order: this.prompt.config.order,
        };
    }
    /**
     * Handle config updates from sidebar
     */
    handleUpdatedConfig(updatedConfig) {
        const targetPromptId = updatedConfig.targetPromptId;
        if (!targetPromptId || this.prompt.promptId !== targetPromptId) {
            return;
        }
        if (!this.isConfigurableRole() || !('config' in this.prompt)) {
            return;
        }
        const configChanged = this.hasConfigChanged(updatedConfig);
        if (configChanged) {
            this.updatePromptConfig(updatedConfig);
        }
    }
    /**
     * Check if prompt role is configurable
     */
    isConfigurableRole() {
        return isMemoryTypePrompt(this.prompt);
    }
    /**
     * Check if config has changed
     */
    hasConfigChanged(updatedConfig) {
        if (!('config' in this.prompt)) {
            return false;
        }
        return this.prompt.config.length !== updatedConfig.length ||
            this.prompt.config.order !== updatedConfig.order;
    }
    /**
     * Update prompt config and save
     */
    updatePromptConfig(updatedConfig) {
        if (!('config' in this.prompt)) {
            return;
        }
        this.prompt.config = {
            length: updatedConfig.length,
            order: updatedConfig.order,
        };
        if (this.onSavePrompt) {
            this.onSavePrompt({ instruction: this.prompt });
        }
    }
    constructor(AgentSegmentService, AgentContextService, AiAgentSidebarStateService, clipboard) {
        //#region Consts
        this.promptRoles = selectableRoles;
        this.roleIconMap = ROLE_ICON_MAP; // Expose static map for template access
        //#endregion
        //#region States
        this.isOpen = false;
        this.isEditable = false;
        this.isVariable = false;
        this.hasGuardrails = false; // Computed property for guard rail visibility
        this.selectedHistoryVariable = '';
        this.historyVariableOptions = [];
        this.variableLength = 50;
        this.variableOrder = 'desc';
        this.isFeatureEnabled = false; // Feature toggle state
        //#endregion
        //#region Lifecycle Hooks
        this.$onInit = async () => {
            // Check if feature is enabled
            this.isFeatureEnabled = await BuilderFeatures.isAiAgentSharedMemoryManipulationEnabled();
            this.isEditable = this.prompt.type !== role.shortTermMemory;
            this.isVariable = this.prompt.type === role.variable;
            this.isOpen = this.isEditable && this.isPromptOpen || false;
            this.hasGuardrails = this.isGuardrailApplicable(this.prompt.type);
            // Load history variables for short-term-memory or variable prompts
            if (isMemoryTypePrompt(this.prompt)) {
                this.loadHistoryVariables();
            }
        };
        this.$doCheck = () => {
            const sidebarData = this.AiAgentSidebarStateService.getData();
            const keysToClear = [];
            if (sidebarData?.selectedVariable) {
                this.handleSelectedVariable(sidebarData);
                keysToClear.push('selectedVariable');
                keysToClear.push('targetPromptId');
                keysToClear.push('selectedVariableHistoryLength');
            }
            if (sidebarData?.updatedConfig) {
                this.handleUpdatedConfig(sidebarData.updatedConfig);
                keysToClear.push('updatedConfig');
            }
            if (keysToClear.length > 0) {
                this.AiAgentSidebarStateService.clearData(keysToClear);
            }
        };
        //#region Methods
        this.onPromptChange = (event) => {
            if ('content' in this.prompt) {
                this.prompt.content = event.detail.value;
            }
        };
        this.onRoleChange = (actualRole) => {
            if (this.onSavePrompt) {
                // Update computed properties
                this.isEditable = actualRole !== role.shortTermMemory;
                this.isVariable = actualRole === role.variable;
                this.hasGuardrails = this.isGuardrailApplicable(actualRole);
                // If changing to variable, initialize with config only if feature is enabled
                if (this.isFeatureEnabled && actualRole === role.variable && 'content' in this.prompt) {
                    const newVariablePrompt = {
                        ...this.prompt,
                        type: role.variable,
                        config: {
                            length: this.variableLength,
                            order: 'desc',
                        },
                    };
                    this.onSavePrompt({ instruction: newVariablePrompt });
                }
                else {
                    // For other roles or variable without feature, just update the role
                    this.prompt.type = actualRole;
                    this.onSavePrompt({ instruction: this.prompt });
                }
                this.AgentSegmentService.trackAiAgentBlock(BUILDER_AGENT_CHATHISTORY_CHANGED, { chatType: actualRole });
            }
        };
        this.deletePrompt = (promptId) => {
            if (this.onDeletePrompt) {
                this.onDeletePrompt({ instructionId: promptId });
            }
        };
        this.copyPrompt = (promptId) => {
            if (this.onCopyPrompt) {
                this.onCopyPrompt({ instructionId: promptId });
            }
        };
        this.onToggleExpand = (event) => {
            if (!event.target.className.includes('prompt-input') || !this.isEditable) {
                return;
            }
            this.isOpen = !this.isOpen;
        };
        this.onBlurPrompt = () => {
            if (this.onSavePrompt) {
                this.onSavePrompt({ instruction: this.prompt });
            }
        };
        /**
         * Load available history variables from AgentContextService
         */
        this.loadHistoryVariables = () => {
            const contexts = this.AgentContextService.getAllContexts();
            this.historyVariableOptions = contexts.map(context => ({
                value: context.agentContext,
                label: `${context.stateName || context.stateId} - {{${context.agentContext}}}`,
            }));
        };
        /**
         * Open sidebar to select history variable
         */
        this.openHistoryVariableSidebar = (event) => {
            event.stopPropagation();
            // Check if feature is enabled
            if (!this.isFeatureEnabled) {
                return;
            }
            // Extract current variable from content if it exists
            let currentVariable = '';
            if ('content' in this.prompt && this.prompt.content) {
                currentVariable = extractVariableName(this.prompt.content);
            }
            // Ensure promptIndex is a valid number (may be undefined if not passed from parent)
            const safePromptIndex = typeof this.promptIndex === 'number' ? this.promptIndex : null;
            this.AiAgentSidebarStateService.open('historyVariable', {
                currentVariable: currentVariable,
                targetPromptId: this.prompt.promptId, // Pass the ID of the prompt being edited
                promptIndex: safePromptIndex, // Pass the index for extracting used variables
                currentStateId: this.currentStateId, // Pass the current agent's stateId to exclude from list
            });
        };
        /**
         * Handle history variable selection from dropdown
         */
        this.onHistoryVariableChange = (event) => {
            this.selectedHistoryVariable = event.detail.value;
            this.AgentSegmentService.trackAiAgentBlock(BUILDER_AGENT_CHATHISTORY_CHANGED, {
                selectedVariable: this.selectedHistoryVariable,
            });
        };
        /**
         * Handle history variable selection from sidebar
         */
        this.onHistoryVariableSelect = (variable) => {
            if (variable?.agentContext) {
                this.selectedHistoryVariable = variable.agentContext;
                this.AgentSegmentService.trackAiAgentBlock(BUILDER_AGENT_CHATHISTORY_CHANGED, {
                    selectedVariable: this.selectedHistoryVariable,
                });
            }
        };
        /**
         * Copy history variable to clipboard
         */
        this.copyHistoryVariable = (event) => {
            event.stopPropagation();
            if (this.selectedHistoryVariable) {
                this.clipboard.copyText(`{{${this.selectedHistoryVariable}}}`);
            }
        };
        /**
         * Manage history settings (placeholder for future implementation)
         */
        this.onManageHistory = (event) => {
            event.stopPropagation();
            // Future: open modal to manage history settings
        };
        /**
         * Open sidebar to configure history settings
         */
        this.openHistoryConfigSidebar = (event) => {
            event.stopPropagation();
            // Check if feature is enabled
            if (!this.isFeatureEnabled) {
                return;
            }
            // Extract current config from shortTermMemory prompt
            let currentConfig = {
                length: this.variableLength,
                order: 'desc'
            };
            if ('config' in this.prompt) {
                currentConfig = {
                    length: this.prompt.config.length || this.variableLength,
                    order: this.prompt.config.order || 'desc'
                };
            }
            // Get selected variable from prompt content to find its historyLength
            let maxHistoryLength = 100; // Default max
            if ('content' in this.prompt && this.prompt.content) {
                const variableName = extractVariableName(this.prompt.content);
                if (variableName) {
                    // Find the context entry for this variable to get its historyLength
                    const contexts = this.AgentContextService.getAllContexts();
                    const context = contexts.find(c => c.agentContext === variableName);
                    if (context?.historyLength) {
                        maxHistoryLength = context.historyLength;
                    }
                }
            }
            this.AiAgentSidebarStateService.open('historyConfig', {
                currentConfig: currentConfig,
                promptId: this.prompt.promptId,
                maxHistoryLength: maxHistoryLength, // Pass max history length from source agent
            });
        };
        /**
         * Handle variable length change
         */
        this.onVariableLengthChange = (event) => {
            if (!event.detail)
                return;
            const value = parseInt(event.detail.value, 10);
            if (!isNaN(value) && value >= 1) {
                this.variableLength = value;
                if (this.isVariable && 'config' in this.prompt && this.prompt.config && this.onSavePrompt) {
                    this.prompt.config.length = value;
                    this.onSavePrompt({ instruction: this.prompt });
                }
            }
        };
        /**
         * Handle variable order change
         */
        this.onVariableOrderChange = (event) => {
            if (!event.detail)
                return;
            this.variableOrder = event.detail.value;
            if (this.isVariable && 'config' in this.prompt && this.prompt.config && this.onSavePrompt) {
                this.onSavePrompt({ instruction: this.prompt });
            }
        };
        /**
         * Handle variable selection change
         */
        this.onVariableSelect = (variable) => {
            if (variable?.agentContext && this.isVariable && 'content' in this.prompt && this.onSavePrompt) {
                this.selectedHistoryVariable = variable.agentContext;
                this.prompt.content = `{{${variable.agentContext}}}`;
                this.onSavePrompt({ instruction: this.prompt });
                this.AgentSegmentService.trackAiAgentBlock(BUILDER_AGENT_CHATHISTORY_CHANGED, {
                    selectedVariable: this.selectedHistoryVariable,
                });
            }
        };
        this.AgentSegmentService = AgentSegmentService;
        this.AgentContextService = AgentContextService;
        this.AiAgentSidebarStateService = AiAgentSidebarStateService;
        this.clipboard = clipboard;
    }
    //#endregion
    // #region Helpers
    isGuardrailApplicable(roleType) {
        return roleType === role.system || roleType === role.ai;
    }
};
//#endregion
PromptInput.$inject = ['AgentSegmentService', 'AgentContextService', 'AiAgentSidebarStateService', 'clipboard'];
PromptInput = __decorate([
    Component({
        controllerAs: '$ctrl',
        template: view,
        bindings: {
            onDeletePrompt: '&?',
            onCopyPrompt: '&?',
            onSavePrompt: '&?',
            onOpenGuardrailModal: '&?',
            prompt: '<',
            promptIndex: '<?',
            isPromptOpen: '<?',
            currentStateId: '<?', // State ID of the current agent being edited
        },
    }),
    __metadata("design:paramtypes", [typeof (_a = typeof AgentSegmentService !== "undefined" && AgentSegmentService) === "function" ? _a : Object, typeof (_b = typeof AgentContextService !== "undefined" && AgentContextService) === "function" ? _b : Object, typeof (_c = typeof AiAgentSidebarStateService !== "undefined" && AiAgentSidebarStateService) === "function" ? _c : Object, Object])
], PromptInput);
export { PromptInput };
